/**
 * Request cache with localStorage persistence
 * Caches requests in memory and localStorage to prevent duplicate API calls
 */

interface CacheEntry {
  promise: Promise<any>;
  timestamp: number;
}

interface LocalStorageCache {
  data: any;
  timestamp: number;
}

class RequestCache {
  private cache = new Map<string, CacheEntry>();
  // Concurrent-request coalescing map. Unlike `cache`, entries here are cleared
  // the instant the request settles — no TTL, no localStorage — so it ONLY
  // collapses requests that overlap in time. See `inflight()`.
  private inflightMap = new Map<string, Promise<any>>();
  private readonly CACHE_DURATION = 300000; // 5 minutes
  private readonly STORAGE_PREFIX = 'archive_cache_';

  /**
   * De-duplicate *concurrent* identical requests without any persistent
   * caching. If a request for `key` is already in flight, the caller gets the
   * same promise instead of firing a second network call; once it settles the
   * key is released so the next call fetches fresh.
   *
   * This is the right tool for data that must stay fresh (feeds, single posts)
   * but should never be fetched twice at once — e.g. React StrictMode's
   * double-invoked effects in dev, or two components mounting on the same tick.
   * On slow connections the in-flight window is long, so this is where most
   * real duplicate downloads get eliminated.
   */
  inflight<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    const existing = this.inflightMap.get(key);
    if (existing) return existing as Promise<T>;

    const promise = requestFn().finally(() => {
      this.inflightMap.delete(key);
    });
    this.inflightMap.set(key, promise);
    return promise;
  }

  /**
   * Get data from localStorage
   */
  private getFromStorage<T>(key: string): T | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_PREFIX + key);
      if (!stored) return null;

      const parsed: LocalStorageCache = JSON.parse(stored);
      const now = Date.now();

      // Check if cache is still valid
      if (now - parsed.timestamp < this.CACHE_DURATION) {
        return parsed.data as T;
      }

      // Remove expired cache
      localStorage.removeItem(this.STORAGE_PREFIX + key);
      return null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  }

  /**
   * Save data to localStorage
   */
  private saveToStorage(key: string, data: any): void {
    try {
      const cacheData: LocalStorageCache = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(this.STORAGE_PREFIX + key, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  /**
   * Read a cached value synchronously, without starting a request.
   *
   * Lets a hook seed its state on the very first render when the data is
   * already cached, so revisiting a page (back/forward, or re-entering from
   * another route) paints immediately instead of flashing a skeleton and
   * shifting the layout under the user. Returns null on a miss — the caller
   * then fetches as normal.
   */
  peek<T>(key: string): T | null {
    return this.getFromStorage<T>(key);
  }

  /**
   * Get or create a cached request
   */
  get<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    const now = Date.now();
    
    // Check localStorage first
    const storedData = this.getFromStorage<T>(key);
    if (storedData) {
      return Promise.resolve(storedData);
    }

    // Check memory cache
    const cached = this.cache.get(key);
    if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
      return cached.promise;
    }

    // Create new request and cache it
    const promise = requestFn().then((data) => {
      // Save to localStorage on success
      this.saveToStorage(key, data);
      return data;
    }).finally(() => {
      // Remove from memory cache after completion
      setTimeout(() => {
        this.cache.delete(key);
      }, this.CACHE_DURATION);
    });

    this.cache.set(key, {
      promise,
      timestamp: now
    });

    return promise;
  }

  /**
   * Clear all cached requests (memory and localStorage)
   */
  clear() {
    this.cache.clear();
    
    // Clear all localStorage entries with our prefix
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.STORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }

  /**
   * Clear specific cached request (memory and localStorage)
   */
  delete(key: string) {
    this.cache.delete(key);
    try {
      localStorage.removeItem(this.STORAGE_PREFIX + key);
    } catch (error) {
      console.error('Error deleting from localStorage:', error);
    }
  }

  /**
   * Clear all cached requests whose key starts with the given prefix
   * (memory and localStorage). Useful for invalidating a family of
   * cached entries (e.g. all creator profiles) after a mutation.
   */
  deleteByPrefix(prefix: string) {
    // Memory cache
    for (const key of Array.from(this.cache.keys())) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }

    // localStorage
    try {
      const storagePrefix = this.STORAGE_PREFIX + prefix;
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(storagePrefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error deleting from localStorage by prefix:', error);
    }
  }
}

export const requestCache = new RequestCache();
